import React, { useState } from 'react';
import classnames from 'classnames/bind';
import { Button } from '../../components/Button';
import { Loader } from '../../components/Loader';
import { PageHeader } from '../../components/PageHeader';
import { SmallNotice } from '../../components/SmallNotice';
import { InfoModal } from '../../components/InfoModal';
import { useUser } from '../../shared/use-user';
import { useDocumentTitle } from '../../shared/use-document-title';
import { useCurrentSubscription } from '../../shared/use-current-subscription';
import { useAuthentication } from '../../shared/use-authentication';
import { cancelSubscription } from '../../../lib/use-cases/cancel-subscription';
import { deleteAccount } from '../../../lib/use-cases/delete-account';
import { ACTIVE } from '../../../lib/shared/subscription-statuses';
import profileDesktopImageSrc from '../../assets/images/page/profile-desktop.png';
import profileMobileImageSrc from '../../assets/images/page/profile-mobile.png';
import styles from './ProfileSettingsPage.css';

const PRE_DELETE_ACCOUNT_MODAL = 'preDeleteAccountModal';
const PRE_CANCEL_SUBSCRIPTION_MODAL = 'preCancelSubscriptionModal';
const SUBSCRIPTION_CANCELED_MODAL = 'subscriptionCanceledModal';

const cx = classnames.bind(styles);

function ProfileSettingsPage() {
    const {userData} = useUser();
    const {signOut} = useAuthentication();
    const {subscriptionData, isLoading, loadSubscriptionData} = useCurrentSubscription();
    const [modal, setModal] = useState({type: ''});
    const [isPending, setIsPending] = useState(false);
    const isActiveSubscription = subscriptionData.status === ACTIVE;

    useDocumentTitle('Profile settings');

    const openModalForDeleteAccount = () => {
        setModal({type: PRE_DELETE_ACCOUNT_MODAL});
    };

    const openModalForCancelSubscription = () => {
        setModal({type: PRE_CANCEL_SUBSCRIPTION_MODAL});
    };

    const closeModal = () => {
        setModal({type: ''});
    };

    const handleDeleteAccount = async () => {
        setIsPending(true);

        const isSuccess = await deleteAccount();

        if (isSuccess) {
            signOut();
        } else {
            alert('Fail');
        }

        setIsPending(false);
    };

    const handleCancelSubscription = async () => {
        try {
            setIsPending(true);

            await cancelSubscription(subscriptionData.id);

            setModal({type: SUBSCRIPTION_CANCELED_MODAL});
            loadSubscriptionData();
        } catch (e) {
            alert(e?.message || 'Fail cancel subscription');
        } finally {
            setIsPending(false);
        }
    };

    const renderUserInformation = () => {
        if (isLoading) {
            return (
                <div className={styles.loaderWrapper}>
                    <Loader variant='secondary' />
                </div>
            );
        };

        return (
            <div className={styles.information}>
                <div className={styles.box}>
                    <p className={styles.fieldName}>
                        Email
                    </p>
                    <p className={styles.fieldValue}>
                        {userData.email || '-'}
                    </p>
                </div>
                {Boolean(subscriptionData.id) && (
                    <div className={styles.box}>
                        <p className={styles.subscriptionBlockTitle}>
                            Subscription status
                        </p>
                        <ul className={styles.subscriptionList}>
                            <li className={styles.subscriptionItem}>
                                <span>
                                    Subscription status
                                </span>
                                <span className={styles[subscriptionData.status]}>
                                    {subscriptionData.status}
                                </span>
                            </li>
                            <li className={styles.subscriptionItem}>
                                <span>
                                    Subscription plan
                                </span>
                                <span>
                                    {subscriptionData.name}
                                </span>
                            </li>
                            <li className={styles.subscriptionItem}>
                                <span>
                                    Billing cycle
                                </span>
                                <span>
                                    {subscriptionData.billingPeriod}
                                </span>
                            </li>
                            <li className={styles.subscriptionItem}>
                                <span>
                                    Amount
                                </span>
                                <span>
                                    {subscriptionData.price}
                                </span>
                            </li>
                            <li className={styles.subscriptionItem}>
                                <span>
                                    Next charge date
                                </span>
                                <span>
                                    {subscriptionData.nextChargeDate || '—'}
                                </span>
                            </li>
                        </ul>
                        {isActiveSubscription && (
                            <div className={styles.subscriptionCancel}>
                                <Button
                                    onClick={openModalForCancelSubscription}
                                    label='Cancel subscription'
                                    type='secondary'
                                    className={styles.buttonCancel}
                                    disabled={subscriptionData.cancelIsFrozen}
                                />
                                {subscriptionData.cancelIsFrozen && (
                                    <SmallNotice
                                        iconName='info'
                                        description='Payment is processing'
                                        className={styles.smallNotice}
                                    />
                                )}
                            </div>
                        )}
                    </div>
                )}
                <div className={styles.box}>
                    <p className={styles.subtitle}>
                        Danger zone
                    </p>
                    {isActiveSubscription && (
                        <SmallNotice
                            iconName='info'
                            description='Your active subscription must be cancelled before you can delete your account.'
                            withBorder
                        />
                    )}
                    <Button
                        onClick={openModalForDeleteAccount}
                        label='Delete your account'
                        type='secondary'
                        className={styles.buttonDelete}
                        disabled={isActiveSubscription}
                        iconLeft={{
                            name: 'trash'
                        }}
                    />
                </div>
            </div>
        )
    };

    return (
        <>
            <PageHeader
                title='Account settings'
                description='Your appearance and basic security'
                desktopImageUrl={profileDesktopImageSrc}
                mobileImageUrl={profileMobileImageSrc}
            />
            {renderUserInformation()}
            <InfoModal
                isOpen={modal.type === PRE_DELETE_ACCOUNT_MODAL}
                onCloseModal={closeModal}
                className={cx('modal', 'preDeleteAccountModal')}
                title='Warning'
                renderButtons={() => (
                    <>
                        <Button
                            onClick={handleDeleteAccount}
                            label='Confirm'
                            type='tertiary'
                            className={styles.modalButton}
                            disabled={isPending}
                        />
                        <Button
                            onClick={closeModal}
                            label='Cancel'
                            type='secondary'
                            className={styles.modalButton}
                        />
                    </>
                )}
            >
                <SmallNotice
                    iconName='alertTriangle'
                    description='Are you sure you want to cancel?'
                />
                <p className={styles.modalDescription}>
                    <strong>Deleting your account will result in the following actions:</strong> <br /><br />
                    Log out: You will be logged out on all devices.<br />
                    Personal Information: All of your personal information will be permanently deleted.<br />
                    Preferences and Payment Information: You will lose all saved preferences and payment details.<br />
                    Newsletters: You will be unsubscribed from all newsletters.<br /><br />
                    <span style={{color: 'var(--error-color)'}}>This action is irreversible and data cannot be restored!</span>
                </p>
            </InfoModal>
            <InfoModal
                isOpen={modal.type === PRE_CANCEL_SUBSCRIPTION_MODAL}
                onCloseModal={closeModal}
                className={cx('modal', 'preCancelSubscriptionModal')}
                title='Canceled subscription?'
                renderButtons={() => (
                    <>
                        <Button
                            onClick={handleCancelSubscription}
                            label='Confirm'
                            type='tertiary'
                            className={styles.modalButton}
                            disabled={isPending}
                        />
                        <Button
                            onClick={closeModal}
                            label='Go back'
                            type='secondary'
                            className={styles.modalButton}
                        />
                    </>
                )}
            >
                <SmallNotice
                    iconName='info'
                    description='Are you sure you want to cancel?'
                    className={styles.smallNotice}
                />
            </InfoModal>
            <InfoModal
                isOpen={modal.type === SUBSCRIPTION_CANCELED_MODAL}
                onCloseModal={closeModal}
                title='Subscription canceled'
                className={cx('modal', 'canceledSubscriptionModal')}
            >
                <SmallNotice
                    iconName='checkedRound'
                    description={`Your plan is active until ${subscriptionData?.validUntilDate}. You may continue to use Futureproof as usual until then.`}
                    className={styles.smallNotice}
                />
            </InfoModal>
        </>
    )
}

export {
    ProfileSettingsPage
};
